import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getAllCategories, getCategoryBySlug } from '@/mocks/categories';
import { getArticlesByCategory, getAllArticles } from '@/mocks/articles';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { WebPageLD } from '@/components/feature/JsonLD';
import type { Article } from '@/mocks/articles';
import type { Category } from '@/mocks/categories';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const allCategories = getAllCategories();

  const [activeSlug, setActiveSlug] = useState(slug || allCategories[0]?.slug || 'videos');
  const [category, setCategory] = useState<Category | undefined>(
    getCategoryBySlug(slug || allCategories[0]?.slug || 'videos')
  );
  const [articles, setArticles] = useState<Article[]>([]);

  useEffect(() => {
    if (slug && slug !== activeSlug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  useEffect(() => {
    const cat = getCategoryBySlug(activeSlug);
    setCategory(cat);
    setArticles(getArticlesByCategory(activeSlug));
    if (cat) {
      document.title = `${cat.name} - 萌白酱Official`;
    }
  }, [activeSlug]);

  const currentCategory = category || allCategories[0];

  return (
    <>
      <WebPageLD
        url={`https://mengbaijiang.com/category/${activeSlug}`}
        name={`${currentCategory?.name || '分类'} - 萌白酱Official`}
        description={currentCategory?.description || '萌白酱内容分类'}
        breadcrumb={[
          { name: '首页', url: 'https://mengbaijiang.com' },
          { name: currentCategory?.name || '分类', url: `https://mengbaijiang.com/category/${activeSlug}` },
        ]}
      />

      <main className="pt-16">
        <div className="relative w-full h-[280px] md:h-[360px] overflow-hidden">
          <img
            src={currentCategory?.coverImage || ''}
            alt={currentCategory?.name}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCategory?.name || '' },
              ]}
            />
            <h1 className="text-2xl md:text-3xl font-heading text-white mt-2">
              {currentCategory?.name}
            </h1>
            <p className="text-sm text-white/70 mt-1 max-w-xl">
              {currentCategory?.description}
            </p>
          </div>
        </div>

        <div className="sticky top-16 z-40 bg-background-50 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center gap-2 overflow-x-auto py-3 flex-wrap">
              {allCategories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveSlug(cat.slug)}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-lg font-heading text-foreground-900">
                共 {articles.length} 篇内容
              </h2>
              <div className="flex items-center gap-2 text-sm text-foreground-500">
                <span>排序：</span>
                <select className="bg-background-100 border border-background-200/70 rounded-lg px-3 py-1.5 text-sm text-foreground-700 focus:outline-none focus:border-primary-300 cursor-pointer">
                  <option>最新发布</option>
                  <option>最多浏览</option>
                  <option>最多点赞</option>
                </select>
              </div>
            </div>

            {articles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {articles.map((article: Article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        {article.isFeatured && (
                          <span className="px-2 py-0.5 bg-accent-100 text-accent-800 text-xs font-medium rounded-full">
                            推荐
                          </span>
                        )}
                        {article.tags.slice(0, 2).map((tag) => (
                          <span key={tag} className="px-2 py-0.5 bg-secondary-100 text-secondary-800 text-xs font-medium rounded-full">
                            {tag}
                          </span>
                        ))}
                      </div>
                      <h3 className="text-base font-heading text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-500 line-clamp-2 mb-3">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-400">
                        <span>{article.publishDate}</span>
                        <div className="flex items-center gap-3">
                          <span><i className="ri-eye-line mr-1"></i>{(article.views / 10000).toFixed(1)}万</span>
                          <span><i className="ri-heart-line mr-1"></i>{(article.likes / 10000).toFixed(1)}万</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-2xl bg-background-100 text-foreground-400 mb-4">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="text-foreground-500">该分类暂无内容</p>
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
}